"use client";

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required accessible name for screen readers (SC 4.1.2). */
  label: string;
  /** When true, the visible glyph keeps its small size and a 44x44 hit area is layered behind it via an absolutely positioned span (SC 2.5.5). */
  compact?: boolean;
  children: ReactNode;
}

const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-primary";

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { label, compact = false, children, className, type = "button", ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        {...rest}
        className={cn(
          "relative inline-flex items-center justify-center rounded-md cursor-pointer transition-colors",
          compact
            ? "before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"
            : "min-w-11 min-h-11",
          FOCUS_RING,
          "disabled:opacity-40 disabled:cursor-not-allowed",
          className,
        )}
      >
        {children}
      </button>
    );
  },
);

export { FOCUS_RING };
